// frontend/src/components/menus/shared/DrawerMenuElements.tsx
import React, { useState } from 'react';
import {
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { appAccessibilityConfig } from '../../../theme/themeConfig';

const sharedBackdropSlotProps = {
  backdrop: {
    sx: {
      backgroundColor: 'rgba(0,0,0,0.45)',
      backdropFilter: 'blur(4px)',
    },
  },
};

type DrawerWithSwipeProps = React.ComponentProps<typeof Drawer> & {
  // When false, descendant StyledListItemText rows do not horizontally swipe;
  // overflowing text is ellipsised and vertical pan reaches the drawer paper.
  allowOverflowSwipe?: boolean;
};

const dataSwipeAttrs = (allowOverflowSwipe: boolean) =>
  ({
    'data-overflow-swipe': allowOverflowSwipe ? 'true' : 'false',
  }) as React.HTMLAttributes<HTMLDivElement>;

// Side drawer (used by the hamburger menu, anchored left).
// Width mirrors StyledBottomDrawer so the two menus feel consistent.
// Full viewport height paper (default temporary Drawer behaviour) — this is
// what reliably scrolls on real touch devices. Do not constrain top/height
// to sit "below the header"; percentage/vh-based height math on a
// fixed-position element does not track the real visible viewport
// consistently across mobile browsers, which silently breaks scrolling.
export const StyledDrawer = styled(
  ({ allowOverflowSwipe = true, ...rest }: DrawerWithSwipeProps) => (
    <Drawer
      {...rest}
      slotProps={sharedBackdropSlotProps}
      PaperProps={dataSwipeAttrs(allowOverflowSwipe)}
    />
  )
)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '430px',
    maxWidth: 'calc(100vw - 3rem)',
    backgroundColor: theme.palette.background.paper,
    paddingTop: 0,
    borderRadius: 0,
    overflowY: 'auto',
    touchAction: 'pan-y',
    transition: appAccessibilityConfig.transitions.standard,
    '&::-webkit-scrollbar': { display: 'none' },
    '&[data-overflow-swipe="false"]': {
      overflowX: 'hidden',
    },
  },
}));

// Bottom drawer (BottomMenu): right edge should track the trigger FAB's right
// edge. Positioning is implementation-supplied via paperPositionSx (the FAB's
// own position varies per page), with sane defaults matching a fixed FAB at
// right: 10% of viewport.
type BottomDrawerProps = DrawerWithSwipeProps & {
  paperPositionSx?: object;
};

export const defaultBottomDrawerPositionSx = {
  left: 'auto',
  right: '1rem',
  bottom: '40px',
  '@media (min-width: 601px)': {
    right: '12%',
  },
  '@media (min-width: 1400px)': {
    right: '10.9%',
  },
  '@media (min-width: 1900px)': {
    right: '10.6%',
  },
};

export const StyledBottomDrawer = styled(
  ({
    allowOverflowSwipe = true,
    paperPositionSx = defaultBottomDrawerPositionSx,
    ...rest
  }: BottomDrawerProps) => (
    <Drawer
      {...rest}
      anchor="bottom"
      slotProps={sharedBackdropSlotProps}
      PaperProps={{
        ...dataSwipeAttrs(allowOverflowSwipe),
        sx: paperPositionSx,
      }}
    />
  )
)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '430px',
    maxWidth: 'calc(100vw - 3rem)',
    maxHeight: '80vh',
    backgroundColor: theme.palette.background.paper,
    paddingTop: '0.5rem',
    paddingBottom: '2rem',
    borderRadius: '2px',
    // mirrors colors.shadows.powerInsetBolder onto the bottom edge
    boxShadow: `var(--Paper-shadow), 0px -12px 8px -10px rgba(0,0,0,0.5) inset`,
    borderBottom: `1px solid ${theme.palette.grey[900]}`,
    transition: appAccessibilityConfig.transitions.standard,
    '&::-webkit-scrollbar': { display: 'none' },
  },
}));

export const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean; component?: any; to?: string }>(({ theme, active }) => ({
  margin: 0,
  padding: '0.2rem 0.1rem',
  borderRadius: 0,
  backgroundColor: active ? theme.palette.primary.main : 'transparent',
  textDecoration: 'none',
  color: 'inherit',
  width: '100%',
  '&:hover': { backgroundColor: theme.palette.primary.light },
  '&:focus-within': {
    backgroundColor: theme.palette.primary.light,
    outline: appAccessibilityConfig.focusOutline,
    outlineOffset: appAccessibilityConfig.focusOffset,
  },
}));

// Label container. Behaviour follows the parent drawer's allowOverflowSwipe:
//   true  -> overflow scrolls horizontally on touch, vertical pan still reaches the drawer
//   false -> overflow is hidden with ellipsis, vertical pan propagates to the drawer
export const StyledListItemText = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  minWidth: 0,
  '& .MuiListItemText-primary': {
    fontSize: '2rem',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
    color: active ? theme.palette.grey[50] : theme.palette.text.primary,
    transition: '0.2s',
  },
  '[data-overflow-swipe="true"] &': {
    overflowX: 'auto',
    touchAction: 'pan-x pan-y',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  '[data-overflow-swipe="false"] &': {
    overflowX: 'hidden',
    touchAction: 'auto',
    '& .MuiListItemText-primary': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
}));

export const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  color: active ? 'white' : 'inherit',
  minWidth: '56px',
  fontSize: '2rem',
  marginLeft: '1rem',
  transition: '0.2s',
}));

// A single row inside a DrawerSubMenu's expanded section.
export interface DrawerSubMenuItem {
  label: string;
  icon?: React.ReactElement;
  onClick: () => void;
}

interface DrawerSubMenuProps {
  icon: React.ReactElement;
  label: string;
  items: DrawerSubMenuItem[];
  // Whether the section starts expanded. Defaults closed - this is a
  // generic building block used for optional/secondary content (first
  // consumer: Header's "Help" section), so it should never claim space
  // in the drawer by default.
  defaultOpen?: boolean;
}

// Generic collapsible sub-section for a drawer's List: a toggle row (icon +
// label + chevron) that expands/collapses an arbitrary list of child rows.
// Reuses StyledListItem/StyledListItemText/StyledListItemIcon so expanded
// rows match every other row in the drawer. Note: MUI's Collapse does not
// unmount its children by default when closed - if a future consumer needs
// closed-state content to be absent from the DOM (not just hidden), pass
// through Collapse's own unmountOnExit rather than assuming it here.
export const DrawerSubMenu: React.FC<DrawerSubMenuProps> = ({
  icon,
  label,
  items,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <>
      <StyledListItem
        disableGutters
        onClick={() => setOpen((prev) => !prev)}
        sx={{ cursor: 'pointer' }}
        aria-expanded={open}
      >
        <StyledListItemIcon>{icon}</StyledListItemIcon>
        <StyledListItemText primary={label} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </StyledListItem>
      <Collapse in={open} timeout="auto">
        <List disablePadding>
          {items.map((item) => (
            <StyledListItem
              key={item.label}
              disableGutters
              onClick={item.onClick}
              sx={{ cursor: 'pointer', pl: 2 }}
            >
              {item.icon && (
                <StyledListItemIcon>{item.icon}</StyledListItemIcon>
              )}
              <StyledListItemText primary={item.label} />
            </StyledListItem>
          ))}
        </List>
      </Collapse>
    </>
  );
};
